
import { Client, Project, TimeEntry, PayPeriod, User, PayPeriodStatus } from '../types';

// Storage Keys
const KEYS = {
  CLIENTS: 'ts_clients',
  PROJECTS: 'ts_projects',
  ENTRIES: 'ts_entries',
  PERIODS: 'ts_periods',
  USER: 'ts_current_user',
  USERS_REGISTRY: 'ts_all_users'
};

const get = <T,>(key: string): T[] => {
  const data = localStorage.getItem(key);
  return data ? JSON.parse(data) : [];
};

const save = <T,>(key: string, data: T[]): void => {
  localStorage.setItem(key, JSON.stringify(data));
};

export const db = {
  // User Management
  getUsers: () => get<User>(KEYS.USERS_REGISTRY),
  
  getCurrentUser: (): User | null => {
    const u = localStorage.getItem(KEYS.USER);
    return u ? JSON.parse(u) : null;
  },

  login: (email: string, name?: string, hourlyRate?: number): User => {
    const users = get<User>(KEYS.USERS_REGISTRY);
    const existing = users.find(u => u.email.toLowerCase() === email.toLowerCase());

    if (existing) {
      localStorage.setItem(KEYS.USER, JSON.stringify(existing));
      return existing;
    }

    // Register new user if not found
    const newUser: User = { 
      id: `user-${Date.now()}`, 
      email: email.toLowerCase(), 
      name: name || email.split('@')[0], 
      hourlyRate: hourlyRate || 50,
      autoMarkPaid: false 
    };
    
    users.push(newUser);
    save(KEYS.USERS_REGISTRY, users);
    localStorage.setItem(KEYS.USER, JSON.stringify(newUser));
    return newUser;
  },

  logout: () => {
    localStorage.removeItem(KEYS.USER);
  },

  updateUser: (user: User) => {
    localStorage.setItem(KEYS.USER, JSON.stringify(user));
    const users = get<User>(KEYS.USERS_REGISTRY);
    const idx = users.findIndex(u => u.id === user.id);
    if (idx > -1) {
      users[idx] = user;
      save(KEYS.USERS_REGISTRY, users);
    }
  },

  deleteAccount: () => {
    const user = db.getCurrentUser();
    if (!user) return;

    const users = get<User>(KEYS.USERS_REGISTRY).filter(u => u.id !== user.id);
    save(KEYS.USERS_REGISTRY, users);

    const clients = get<Client>(KEYS.CLIENTS).filter(c => c.userId !== user.id);
    save(KEYS.CLIENTS, clients);
    
    const entries = get<TimeEntry>(KEYS.ENTRIES).filter(e => e.userId !== user.id);
    save(KEYS.ENTRIES, entries);
    
    const periods = get<PayPeriod>(KEYS.PERIODS).filter(p => p.userId !== user.id);
    save(KEYS.PERIODS, periods);

    localStorage.removeItem(KEYS.USER);
  },

  // Scoped Data Fetching
  getClients: (userId: string) => get<Client>(KEYS.CLIENTS).filter(c => c.userId === userId),
  
  saveClient: (client: Client) => {
    const clients = get<Client>(KEYS.CLIENTS);
    const idx = clients.findIndex(c => c.id === client.id);
    if (idx > -1) clients[idx] = client;
    else clients.push(client);
    save(KEYS.CLIENTS, clients);
  },

  deleteClient: (clientId: string) => {
    const clients = get<Client>(KEYS.CLIENTS).filter(c => c.id !== clientId);
    save(KEYS.CLIENTS, clients);
  },

  getEntries: (userId: string) => get<TimeEntry>(KEYS.ENTRIES).filter(e => e.userId === userId),
  
  getOpenEntriesForClient: (userId: string, clientId: string) => 
    get<TimeEntry>(KEYS.ENTRIES).filter(e => e.userId === userId && e.clientId === clientId && !e.payPeriodId),
  
  saveEntries: (userId: string, entries: TimeEntry[]) => {
    const allExisting = get<TimeEntry>(KEYS.ENTRIES);
    const newEntryIds = new Set(entries.map(e => e.id));
    const otherEntries = allExisting.filter(e => !newEntryIds.has(e.id));
    save(KEYS.ENTRIES, [...otherEntries, ...entries]);
  },
  
  deleteEntry: (id: string) => {
    const all = get<TimeEntry>(KEYS.ENTRIES).filter(e => e.id !== id);
    save(KEYS.ENTRIES, all);
  },

  getPayPeriods: (userId: string) => get<PayPeriod>(KEYS.PERIODS).filter(p => p.userId === userId),
  
  savePayPeriod: (period: PayPeriod) => {
    const periods = get<PayPeriod>(KEYS.PERIODS);
    const idx = periods.findIndex(p => p.id === period.id);
    if (idx > -1) periods[idx] = period;
    else periods.push(period);
    save(KEYS.PERIODS, periods);
  }
};
